import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield } from 'lucide-react';
import { MarkdownContent } from '../components/ui/MarkdownContent';


// Hardcoded Privacy Policy content (Version 2.3)
const PRIVACY_CONTENT = `# Privacy Policy

**Version 2.3**

> **Your Privacy Matters:** This Privacy Policy explains how Wallstreetle collects, uses, stores, and protects your personal information when you use our Service.

---

### 1. Who We Are

Wallstreetle is a daily stock-guessing game operated by Wallstreetle (**we**, **us**, **our**). We respect your privacy and are committed to protecting your personal data.

### 2. Information We Collect

#### 2.1 Account Information
When you create an account, we collect:
- Email address
- Username
- Password (encrypted)

#### 2.2 Game Data
When you play Wallstreetle, we collect:
- Game scores and results
- Other data related to actions in the game and website

#### 2.3 Usage Information
We automatically collect:
- Device information (browser, OS, device type)
- IP address
- Analytics data (page views, clicks, session duration)
- Referral source

#### 2.4 Payment Information
If you subscribe to premium features:
- Payment information is processed by Stripe (we do not store credit card details)
- Subscription status and billing history

#### 2.5 Cookies and Tracking
We use:
- Essential cookies for authentication and game state
- Analytics cookies to understand usage patterns
- Local storage for game progress and preferences
- Advertising cookies to personalize and display targeted advertisements on our Service

#### 2.6 Advertising
We reserve the right to display advertisements on the Service. These advertisements may be personalized based on your usage data, cookies, device information, and browsing history to provide you with more relevant ads. We may partner with third-party advertising networks and ad servers who may also set their own cookies and tracking technologies. You can manage cookie preferences through your browser settings, though this may limit certain functionality of the Service.

---

### 3. Google API Services & User Data

This section applies specifically to users who choose to sign in or create an account using Google (via Google OAuth). Our use of data received from Google APIs will adhere to the Google API Services User Data Policy, including the Limited Use requirements.

#### 3.1 Data Accessed (Google)
When you choose to sign in with Google, we access and **store** the following specific types of Google user data:
- **Google User ID (\`sub\`)**: A unique, persistent identifier used to securely link your Google account to your Wallstreetle account.
- **Email Address (\`email\`)**: Used for account identification, creation, and essential communications.
- **Email Verification Status (\`email_verified\`)**: Indicates whether Google has verified the associated email address.
- **Full Name (\`name\`, \`full_name\`)**: The name associated with your Google account.
- **Profile Picture/Avatar URL (\`picture\`, \`avatar_url\`)**: A URL linking to your profile image hosted by Google.
- **Provider ID (\`provider_id\`)**: A unique identifier provided by the authentication system.
- **Issuer (\`iss\`)**: Identifies the Google authentication server.
- **Phone Verification Status (\`phone_verified\`)**: Indicates whether a phone number is verified on the Google account.

We only request access to data that is essential for the app's core authentication functionality.

#### 3.2 Data Usage (Google)
Your Google user data is used for the following purposes:
- **Authentication**: To allow you to securely sign in to your Wallstreetle account without a separate password.
- **Account Creation & Linking**: To create a new Wallstreetle account or link your Google sign-in to an existing one.
- **Communications**: To send account-related emails (such as verification emails and important updates) to your Google email address, as described in **Section 4**.
- **Security & Integrity**: The **Email Verification Status** and **Phone Verification Status** help to maintain the integrity and security of your account.
- **Advertising & Personalization**: To personalize and deliver targeted advertisements on our Service and to improve ad relevance, as described in **Section 2.6**.

We do not *sell* your Google data to third parties.

#### 3.3 Data Sharing (Google)
We do not sell or rent your Google user data. We only share it with third parties as described below:
- **Supabase**: Your core account data, including your **Google User ID**, **Email Address**, **Full Name**, **Profile Picture/Avatar URL**, and other associated identifiers, are shared with Supabase, our backend service provider. This sharing is *strictly* for the purpose of authentication and storing your core account data.
- **Stripe**: If you choose to subscribe to a premium feature, your **Email Address** (which was obtained from Google) will be shared with Stripe, our payment processor. This is necessary to process your payment, match the subscription to your account, and manage your billing history.
- **Legal Requirements**: We may disclose your information if required by law, as detailed in **Section 5.4**.

#### 3.4 Data Storage & Protection (Google)
Your Google user data (including **Google User ID**, **Email Address**, **Full Name**, and **Profile Picture/Avatar URL**) is stored securely in our Supabase database.

All our data storage and security practices, which apply to *all* user data including Google data, are detailed in **Section 6 (Data Storage & Security)** of this policy. This includes encryption in transit (TLS/SSL), encryption at rest, and strict access controls (Row Level Security).

#### 3.5 Data Retention & Deletion (Google)
Your Google user data is retained as long as your Wallstreetle account is active.

If you delete your account, your data (including Google-provided data) is permanently deleted within 90 days, as detailed in **Section 6.3 (Data Retention)**.

You have the right to request the deletion of your data at any time. The process to request deletion is detailed in **Section 7.3 (Deletion)**.

---

### 4. How We Use Your Information

We use your information (including data collected via Google as described in Section 3) to:
- **Provide the Service**: Enable gameplay, save progress, display leaderboards
- **Personalize Experience**: Remember preferences, suggest categories
- **Advertising & Personalization**: Display personalized advertisements based on your usage data and preferences, as described in **Section 2.6**
- **Improve the Service**: Analyze usage patterns, identify bugs, develop features
- **Communications**: Send verification emails, important updates, premium notifications
- **Security**: Detect fraud, prevent abuse, enforce our Terms & Conditions
- **Legal Compliance**: Comply with applicable laws and regulations
- **Analytics**: Use aggregated, anonymized data for business insights and service improvement

### 5. Data Sharing

#### 5.1 Third-Party Services
We share data with trusted partners to provide and improve the Service:
- **Supabase** (PostgreSQL): Database hosting, authentication (as described in Section 3.3)
- **Stripe**: Payment processing for premium subscriptions (as described in Section 3.3)

We reserve the right to engage additional third-party service providers, change our service providers, or use different vendors as needed to maintain, improve, or scale the Service. We may share your personal information with these service providers as necessary for them to perform their functions. We will update this policy if we materially change our third-party data processors.

#### 5.2 Third-Party Service Disclaimers
Our Service relies on third-party providers including Supabase (database and authentication) and Stripe (payment processing). **We are not responsible for issues, failures, data breaches, or losses arising from these third-party providers.** Use of their services is governed by their own privacy policies and terms of service. We recommend reviewing their terms directly.

#### 5.4 Legal Requirements
We may disclose your information if required by law, court order, or government request.

### 6. Data Storage & Security

#### 6.1 Where We Store Data
- **Database**: Supabase PostgreSQL (encrypted at rest)
- **Location**: Cloud infrastructure (AWS/Google Cloud)
- **Backups**: Regular encrypted backups

#### 6.2 Security Measures
- **Encryption**: TLS/SSL for data in transit, encryption at rest
- **Access Control**: Row Level Security (RLS) policies in database
- **Authentication**: Supabase Auth with secure password hashing
- **Monitoring**: Regular security audits and vulnerability scanning

#### 6.3 Data Retention
- **Deleted Accounts**: Data deleted within 90 days (except legal retention requirements)

### 7. Your Rights

You have the right to:

#### 7.1 Access
Request a copy of your personal data

#### 7.2 Correction
Update or correct inaccurate information

#### 7.3 Deletion
Request deletion of your account and data (subject to legal obligations)

#### 7.4 Data Portability
Export your game data in machine-readable format

#### 7.5 Opt-Out
- Unsubscribe from marketing emails (account-related emails may still be sent)
- Disable analytics cookies (may affect functionality)

#### 7.6 Withdraw Consent
Withdraw consent for data processing (may limit Service availability)

**To exercise your rights**, contact us at [info@wallstreetle.com](mailto:info@wallstreetle.com)

### 8. Children's Privacy

Wallstreetle is **not intended for children under 13**. We do not knowingly collect information from children. If we discover we have collected data from a child under 13, we will delete it immediately.

**COPPA Compliance**: Parents who believe their child has provided information may contact us to request deletion.

### 9. International Users

#### 9.1 GDPR (European Union)
If you are in the EU, you have additional rights:
- Right to object to processing
- Right to restrict processing
- Right to lodge a complaint with your local data protection authority

**Legal Basis**: We process your data based on:
- Contract performance (providing the Service)
- Legitimate interests (improving the Service, security)
- Consent (marketing, optional features)

#### 9.2 CCPA (California)
California residents have the right to:
- Know what personal information is collected
- Know whether personal information is sold (we do not sell data)
- Opt-out of the sale of personal information
- Non-discrimination for exercising privacy rights

### 10. Service Disclaimer

#### 10.1 As-Is Service
Wallstreetle is provided **"as is" and "as available"** without warranties of any kind, either express or implied. We make no warranties regarding uptime, performance, reliability, or fitness for a particular purpose.

#### 10.2 Acceptable Use & Prohibited Conduct
You agree not to:
- Reverse engineer, scrape, or attempt to extract data from the Service
- Attempt unauthorized access or breach security measures
- Use the Service for illegal activities or to facilitate illegal conduct
- Engage in abusive gameplay, cheating, or account manipulation
- Violate others' rights or intellectual property
- Disrupt the Service or its infrastructure

Violation of these terms may result in account suspension or termination without refund.

#### 10.3 Account Suspension & Termination
We reserve the right to suspend or terminate your account at any time, for any reason, including but not limited to violation of these terms, abuse, cheating, or technical necessity. Suspended or terminated accounts are not eligible for refund. Your access to game progress and data may be permanently lost upon termination.

### 11. Limitations of Liability

#### 11.1 No Liability for Damages
**To the fullest extent permitted by law**, Wallstreetle and its officers, employees, and affiliates are not liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service, including but not limited to:
- Service interruptions or downtime (including due to Supabase, Render, or third-party infrastructure failures)
- Data loss, corruption, or unauthorized access
- Bugs, glitches, or technical failures
- Loss of game progress, scores, or accounts
- Third-party breaches or attacks
- Your reliance on information or game data

This limitation applies even if we have been advised of the possibility of such damages.

#### 11.2 Limitation on Refunds
**All subscriptions and purchases are final and non-refundable**, except where required by law. Refund requests must be made within 30 days of purchase and are evaluated on a case-by-case basis.

### 12. Service Modifications & Downtime

We reserve the right to modify, suspend, or discontinue any feature or the entire Service at any time, with or without notice. We are not liable for any loss of access, content, or gameplay resulting from such changes, modifications, or discontinuations.

### 13. Indemnification

You agree to indemnify and hold harmless Wallstreetle, its officers, employees, agents, and affiliates from any and all claims, damages, liabilities, and expenses (including legal fees) arising out of:
- Your use of the Service
- Your violation of these terms
- Your violation of any applicable laws or regulations
- Your violation of others' rights
- Any content you upload or share through the Service

### 14. Aggregated & Anonymized Data

We may collect, use, and share aggregated or anonymized data derived from your use of the Service for analytics, business insights, research, and service improvement purposes, without restriction or compensation to you.

### 15. Force Majeure

Wallstreetle is not liable for any failure or delay in performance under these terms caused by events beyond our reasonable control, including but not limited to cyberattacks, natural disasters, government action, pandemics, or infrastructure failures of third-party providers.

### 16. Financial Advice Disclaimer

**Important**: Wallstreetle is a game for entertainment purposes only. Information, stock data, or educational content provided through the Service is **not financial advice**. We do not recommend any specific investments or trading strategies. Consult a qualified financial advisor before making investment decisions. Past performance does not guarantee future results.

### 17. Governing Law & Jurisdiction

This Privacy Policy and all use of Wallstreetle are governed by the laws of **[Your Jurisdiction]** (e.g., Australia/New South Wales, United States, etc.), without regard to its conflict of law principles. You agree to submit to the exclusive jurisdiction and venue of the courts located in **[Your Jurisdiction]** for any legal disputes.

### 18. Severability

If any provision of this Privacy Policy is found to be unenforceable or invalid, that provision will be modified to the minimum extent necessary to make it enforceable, or if not possible, severed. The remaining provisions will remain in full force and effect.

### 19. Changes to This Policy

We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Effective Date."

**Material Changes**: We will notify you via email or prominent notice in the Service.

**Your Continued Use**: Continued use after changes constitutes acceptance of the updated Privacy Policy. If you do not agree, you must stop using the Service.

### 20. Contact Us

For privacy inquiries, questions, or data requests:

**Email**: [info@wallstreetle.com](mailto:info@wallstreetle.com)
**Response Time**: We aim to respond within 30 days
`;

const PRIVACY_VERSION = '2.3';
const PRIVACY_EFFECTIVE_DATE = new Date('2025-10-23T00:00:00Z');
const PRIVACY_UPDATED_AT = new Date('2025-10-23T00:00:00Z');

export const PrivacyPage: React.FC = () => {

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Wallstreetle | Privacy Policy</title>
        <meta name="description" content="Privacy Policy for Wallstreetle - the stock wordle and finance wordle game. Learn how we collect, use, and protect your data." />
        <meta name="keywords" content="Wallstreetle privacy, privacy policy, data protection, user privacy, stock wordle privacy, finance wordle data policy" />
        <link rel="canonical" href="https://wallstreetle.com/policies/privacy" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | Privacy Policy" />
        <meta property="og:description" content="Privacy Policy for using Wallstreetle, the stock guessing game. Learn about our data practices." />
        <meta property="og:url" content="https://wallstreetle.com/policies/privacy" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Privacy Policy",
            "description": "Privacy Policy for Wallstreetle - the stock wordle and finance wordle game. Information about data collection, usage, and protection.",
            "url": "https://wallstreetle.com/policies/privacy",
            "inLanguage": "en-US",
            "author": {
              "@type": "Person",
              "name": "Wallstreetle Team",
              "url": "https://wallstreetle.com"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Wallstreetle",
              "logo": {
                "@type": "ImageObject",
                "url": "https://wallstreetle.com/logo512.png",
                "width": 512,
                "height": 512
              }
            },
            "datePublished": PRIVACY_EFFECTIVE_DATE.toISOString().split('T')[0],
            "dateModified": PRIVACY_UPDATED_AT.toISOString().split('T')[0],
            "version": PRIVACY_VERSION,
            "isPartOf": {
              "@type": "WebSite",
              "name": "Wallstreetle",
              "url": "https://wallstreetle.com"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://wallstreetle.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Privacy Policy",
                  "item": "https://wallstreetle.com/policies/privacy"
                }
              ]
            }
          })}
        </script>
      </Helmet>
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Privacy Policy
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Version {PRIVACY_VERSION} • Effective {PRIVACY_EFFECTIVE_DATE.toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-card border border-border rounded-lg p-8 shadow-sm">
          <MarkdownContent content={PRIVACY_CONTENT} />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Last updated: {PRIVACY_UPDATED_AT.toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
